import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { selectUser, fetchBoardCategories, selectBoards, selectBoardsLoading } from '../../../redux/WithcookieSlice';
import { usePropertyPanel } from '../PropertyPanelContext';
import PaddingControl from '../CommonProperties/PaddingControl';
import BorderRadiusControl from '../CommonProperties/BorderRadiusControl';
import StyleControl from '../CommonProperties/StyleControl';
import SkinSelector from '../CommonProperties/SkinSelector';

const BoardProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const companyId = user?.company_id || 21;
  const boardCategories = useSelector(selectBoards) || [];
  const isLoadingBoards = useSelector(selectBoardsLoading);
  
  // PropertyPanelContext에서 필요한 함수 가져오기
  const { devicePropertyTab } = usePropertyPanel();
  
  // 게시판용 디바이스 탭 상태 (PropertyPanelContext의 devicePropertyTab과 동기화)
  const [boardDeviceTab, setBoardDeviceTab] = useState(devicePropertyTab || 'pc');
  
  // devicePropertyTab이 변경될 때 boardDeviceTab도 함께 변경
  React.useEffect(() => {
    setBoardDeviceTab(devicePropertyTab);
  }, [devicePropertyTab]);
  
  // 게시판 카테고리 목록 가져오기
  useEffect(() => {
    if (companyId) {
      dispatch(fetchBoardCategories(companyId));
    }
  }, [companyId, dispatch]);
  
  // 디바이스별 데이터 변경 핸들러
  const handleBoardDeviceDataChange = (field, value) => {
    if (!selectedComponent) return;
    
    
    // 전체 데이터의 깊은 복사
    const existingData = JSON.parse(JSON.stringify(selectedComponent.data || {}));
    
    // 초기 디바이스 객체 설정
    if (!existingData.device) {
      existingData.device = { pc: {}, mobile: {} };
      
      // 기존 값이 있으면 초기 마이그레이션 (양쪽에 같은 값 설정)
      Object.keys(existingData).forEach(key => {
        // device 키와 업데이트할 필드 자체는 복사하지 않음
        if (key !== 'device' && key !== field) {
          existingData.device.pc[key] = existingData[key];
          existingData.device.mobile[key] = existingData[key];
        }
      });
    }
    
    // 현재 디바이스 객체 확인
    if (!existingData.device[boardDeviceTab]) {
      existingData.device[boardDeviceTab] = {};
    }
    
    // 현재 디바이스 데이터 업데이트 (다른 디바이스 데이터는 그대로 유지)
    existingData.device[boardDeviceTab][field] = value;
    
    // 최상위 필드도 함께 업데이트 (전역 속성 유지)
    existingData[field] = value;
    
    
    // Redux 액션으로 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        data: existingData
      }
    }));
  };
  
  // 디바이스별 값 가져오기 함수
  const getDeviceValue = (data, field, defaultValue) => {
    // 데이터가 없으면 기본값 반환
    if (!data) {
      return defaultValue;
    }
    
    try {
      // device 객체가 있고, 현재 선택된 디바이스에 해당 필드가 있는 경우
      if (data.device && 
          data.device[boardDeviceTab] && 
          data.device[boardDeviceTab][field] !== undefined) {
        
        const value = data.device[boardDeviceTab][field];
        return value;
      }
      
      // 디바이스별 설정이 없는 경우, 최상위 필드 확인
      if (data[field] !== undefined) {
        const value = data[field];
        return value;
      }
      
      // 모든 경우에 해당 없으면 기본값 반환
      return defaultValue;
    } catch (err) {
      console.error(`getDeviceValue 오류:`, err);
      return defaultValue;
    }
  };
  
  // 체크박스 변경 핸들러
  const handleCheckboxChange = (field) => (e) => {
    handleBoardDeviceDataChange(field, e.target.checked);
  };
  
  // 컴포넌트 속성 변경 핸들러 (SkinSelector 용)
  const handleComponentPropsChange = (property, value) => {
    if (!selectedComponent) return;
    
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    existingProps[property] = value;
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };
  
  return (
    <>
      <SkinSelector 
        selectedComponent={selectedComponent}
        onSkinChange={(skinId) => handleComponentPropsChange('skin', skinId)}
      />
      
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          게시판 설정
        </div>
        
        <div className="property-control">
          <label style={{ minWidth: '30%' }}>게시판 제목</label>
          <input
            type="text"
            value={getDeviceValue(selectedComponent.data, 'title', '')}
            onChange={(e) => handleBoardDeviceDataChange('title', e.target.value)}
          />
        </div>
        
        <div className="property-control">
          <label style={{ minWidth: '30%' }}>게시판 선택</label>
          {isLoadingBoards ? (
            <div style={{ padding: '8px', color: '#666' }}>게시판 목록 불러오는 중...</div>
          ) : (
            <select
              value={getDeviceValue(selectedComponent.data, 'parameter', '')}
              onChange={(e) => handleBoardDeviceDataChange('parameter', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff'
              }}
            >
              <option value="">게시판을 선택하세요</option>
              {boardCategories.map((board) => (
                <option key={board.id} value={`board${board.id}`}>
                  {board.name} 
                </option>
              ))}
            </select>
          )}
          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
            회사에서 사용 가능한 게시판 목록입니다.
          </small>
        </div>
      </div>
      


    </>
  );
};

export default BoardProperties; 